import React, {useCallback, useEffect, useState} from 'react';
import { Space } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { CommandDto } from '../api';
import CommandsOutputCards from "./commands/CommandsOutputCards";

const CardType = 'CARD';

interface DraggableCardProps {
    cardData: CommandDto;
    index: number;
    onRemoveCommand: (commandDto: CommandDto) => void;

    onUpdateCommand: (commandDto: CommandDto, commandIndex: number) => void;
}

const DraggableCard: React.FC<DraggableCardProps> = ({ cardData, index , onRemoveCommand, onUpdateCommand}) => {
    const handleRemoveCommand = useCallback((commandDto: CommandDto) => {
        onRemoveCommand(commandDto);
    }, [onRemoveCommand]); // onRemoveCommand ist die Abhängigkeit

    const handleUpdateCommand = useCallback((commandDto: CommandDto, index: number) => {
        onUpdateCommand(commandDto, index);
    }, [onUpdateCommand]); // onUpdateCommand ist die Abhängigkeit

    return (
        <Draggable draggableId={CardType + index.toString()} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                >
                    <CommandsOutputCards commandDto={cardData} commandIndex={index} onRemoveCommand={handleRemoveCommand}
                                         onUpdateCommand={handleUpdateCommand}/>
                </div>
            )}
        </Draggable>
    );
};

interface CardListProps {
    cards: CommandDto[];
    onRemoveCommand: (commandDto: CommandDto) => void;

    onUpdateCommand: (commandDto: CommandDto, index: number) => void;
}

const CardList: React.FC<CardListProps> = ({ cards , onRemoveCommand, onUpdateCommand}) => {

    const handleRemoveCommand = useCallback((commandDto: CommandDto) => {
        onRemoveCommand(commandDto);
    }, [onRemoveCommand]); // Abhängigkeit: onRemoveCommand

    // Verwende useCallback für handleUpdateCommand
    const handleUpdateCommand = useCallback((commandDto: CommandDto, index: number) => {
        onUpdateCommand(commandDto, index);
    }, [onUpdateCommand]); // Abhängigkeit: onUpdateCommand

    return (
        <Droppable droppableId="droppable" type={CardType}>
            {(provided, snapshot) => (
                <div ref={provided.innerRef}>
                    {cards.map((card, index) => (
                        <DraggableCard key={index} cardData={card} index={index} onRemoveCommand={handleRemoveCommand}
                                       onUpdateCommand={handleUpdateCommand}/>
                    ))}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
};

interface CreationEditorProps {
    abstractCommands: CommandDto[];
    formCommandDto: CommandDto | undefined;

    onUpdateCommandsData: (updatedCommands: CommandDto[]) => void;
}

const CreationEditor: React.FC<CreationEditorProps> = ({ abstractCommands, formCommandDto , onUpdateCommandsData}) => {
    const [commands, setCommands] = useState<CommandDto[]>([]);

    useEffect(() => {
        setCommands(abstractCommands);
        // Callback wenn sich die Command-Daten geändert haben
        onUpdateCommandsData(abstractCommands);
    }, [abstractCommands]);

    useEffect(() => {
       if(formCommandDto !== undefined){
           addNewCommand(formCommandDto);
       }
    }, [formCommandDto]);


    const addNewCommand = (command: CommandDto) => {
        // Erstelle eine Kopie des aktuellen Zustands
        const newCommands = [...commands];
        // Füge das neue Kommando hinzu
        newCommands.push(command);
        // Aktualisiere den Zustand mit der neuen Kopie
        setCommands(newCommands);
        // Callback wenn sich die Command-Daten geändert haben
        onUpdateCommandsData(newCommands);
    };

    const onDragEnd = (result) => {
        // Logik für das Verschieben der Elemente implementieren
        if (!result.destination) {
            // Das Element wurde außerhalb der Liste fallen gelassen
            return;
        }

        const items = Array.from(commands);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setCommands(items);
        onUpdateCommandsData(items);
    };

    // Verwende useCallback für handleRemoveCommand
    const handleRemoveCommand = useCallback((commandDto: CommandDto) => {
        const updatedCommands = commands.filter((command) => command !== commandDto);
        setCommands(updatedCommands);
        onUpdateCommandsData(updatedCommands);
    }, [commands, setCommands, onUpdateCommandsData]); // Abhängigkeiten: commands, setCommands, onUpdateCommandsData

    // Verwende useCallback für handleUpdateCommand
    const handleUpdateCommand = useCallback((commandDto: CommandDto, index: number) => {
        setCommands(prevCommands => {
            const updatedCommands = prevCommands.map((command, i) =>
                i === index ? commandDto : command
            );
            onUpdateCommandsData(updatedCommands);
            return updatedCommands;
        });
    }, [setCommands, onUpdateCommandsData]);

        return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <CardList cards={commands} onRemoveCommand={handleRemoveCommand} onUpdateCommand={handleUpdateCommand}/>
            </Space>
        </DragDropContext>
    );
};

export default CreationEditor;